import type { SQLiteDatabase } from 'expo-sqlite';

import { newId, now } from '@/db/ids';
import type { QuoteRow, QuoteStatus } from '@/db/types';
import { bumpDataRevision } from '@/lib/data-revision';
import { addExpense } from '@/lib/expenses';

/**
 * Quotes — contractor / vendor estimates the homeowner collects before
 * committing to work. See schema v47 for the data-model rationale.
 *
 * Categories intentionally reuse EXPENSE_CATEGORIES (imported by the
 * form screens) so a converted quote lands in the same bucket the
 * expense dashboards already chart.
 */

export const QUOTE_STATUS_META: Record<
  QuoteStatus,
  { label: string; tint: string }
> = {
  // Tints reference the palette by hex to keep this module UI-light;
  // the pages map them onto chips. forest=open/accepted-ish states,
  // clay=declined, faint=expired, honey=converted.
  open: { label: 'Open', tint: '#2C4738' },
  accepted: { label: 'Accepted', tint: '#5C7E55' },
  declined: { label: 'Declined', tint: '#B05F3C' },
  expired: { label: 'Expired', tint: '#A79C88' },
  converted: { label: 'Converted', tint: '#C2842A' },
};

export interface NewQuoteInput {
  propertyId: string;
  systemId?: string | null;
  contactId?: string | null;
  project?: string | null;
  category: string;
  vendor?: string | null;
  description?: string | null;
  amount_cents: number;
  quoted_on: number;
  valid_until?: number | null;
  status?: QuoteStatus;
  quote_doc_id?: string | null;
  notes?: string | null;
}

export async function addQuote(
  db: SQLiteDatabase,
  input: NewQuoteInput
): Promise<string> {
  const id = newId();
  const t = now();
  await db.runAsync(
    `INSERT INTO quote (
      id, property_id, system_id, contact_id, project, category, vendor,
      description, amount_cents, quoted_on, valid_until, status,
      quote_doc_id, notes, created_at, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    id,
    input.propertyId,
    input.systemId ?? null,
    input.contactId ?? null,
    input.project?.trim() || null,
    input.category,
    input.vendor?.trim() || null,
    input.description?.trim() || null,
    input.amount_cents,
    input.quoted_on,
    input.valid_until ?? null,
    input.status ?? 'open',
    input.quote_doc_id ?? null,
    input.notes?.trim() || null,
    t,
    t
  );
  bumpDataRevision();
  return id;
}

export interface UpdateQuoteInput {
  systemId?: string | null;
  contactId?: string | null;
  project?: string | null;
  category?: string;
  vendor?: string | null;
  description?: string | null;
  amount_cents?: number;
  quoted_on?: number;
  valid_until?: number | null;
  status?: QuoteStatus;
  quote_doc_id?: string | null;
  converted_expense_id?: string | null;
  notes?: string | null;
}

export async function updateQuote(
  db: SQLiteDatabase,
  id: string,
  input: UpdateQuoteInput
): Promise<void> {
  const t = now();
  const sets: string[] = [];
  const args: (string | number | null)[] = [];
  const fields: (keyof UpdateQuoteInput)[] = [
    'systemId', 'contactId', 'project', 'category', 'vendor', 'description',
    'amount_cents', 'quoted_on', 'valid_until', 'status', 'quote_doc_id',
    'converted_expense_id', 'notes',
  ];
  const COL: Partial<Record<keyof UpdateQuoteInput, string>> = {
    systemId: 'system_id',
    contactId: 'contact_id',
  };
  for (const f of fields) {
    if (input[f] !== undefined) {
      sets.push(`${COL[f] ?? f} = ?`);
      args.push((input[f] as string | number | null) ?? null);
    }
  }
  if (sets.length === 0) return;
  sets.push('updated_at = ?');
  args.push(t);
  args.push(id);
  await db.runAsync(`UPDATE quote SET ${sets.join(', ')} WHERE id = ?`, ...args);
  bumpDataRevision();
}

export async function deleteQuote(db: SQLiteDatabase, id: string): Promise<void> {
  const t = now();
  await db.runAsync(
    `UPDATE quote SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    id
  );
  bumpDataRevision();
}

/** Bulk status set (Wave 61 mass-edit). Never touches 'converted' rows
 *  — that state is terminal and only the Convert flow creates it. */
export async function bulkSetQuoteStatus(
  db: SQLiteDatabase,
  ids: string[],
  status: QuoteStatus
): Promise<void> {
  if (ids.length === 0 || status === 'converted') return;
  const t = now();
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    await db.runAsync(
      `UPDATE quote SET status = ?, updated_at = ?
       WHERE id IN (${chunk.map(() => '?').join(',')}) AND status != 'converted'`,
      status,
      t,
      ...chunk
    );
  }
  bumpDataRevision();
}

/** Bulk soft-delete (Wave 61 mass-edit). */
export async function bulkDeleteQuotes(db: SQLiteDatabase, ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const t = now();
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    await db.runAsync(
      `UPDATE quote SET deleted_at = ?, updated_at = ? WHERE id IN (${chunk.map(() => '?').join(',')})`,
      t,
      t,
      ...chunk
    );
  }
  bumpDataRevision();
}

export async function getQuoteById(
  db: SQLiteDatabase,
  id: string
): Promise<QuoteRow | null> {
  return await db.getFirstAsync<QuoteRow>(
    `SELECT * FROM quote WHERE id = ? AND deleted_at IS NULL`,
    id
  );
}

export async function listQuotes(
  db: SQLiteDatabase,
  propertyId: string
): Promise<QuoteRow[]> {
  return await db.getAllAsync<QuoteRow>(
    `SELECT * FROM quote
     WHERE property_id = ? AND deleted_at IS NULL
     ORDER BY quoted_on DESC`,
    propertyId
  );
}

/**
 * Group quotes by project for the comparison view. Quotes without a
 * project each get their own single-item group keyed by their id so
 * everything renders through one code path. Groups are ordered by the
 * most recent quote inside them; quotes within a group are ordered
 * cheapest-first so the price spread reads naturally.
 */
export interface QuoteGroup {
  /** Project label, or null for an ungrouped single quote. */
  project: string | null;
  quotes: QuoteRow[];
  minCents: number;
  maxCents: number;
  latestAt: number;
}

export function groupQuotesByProject(quotes: QuoteRow[]): QuoteGroup[] {
  const byKey = new Map<string, QuoteRow[]>();
  for (const q of quotes) {
    // Case-insensitive project matching so "Roof" and "roof" cluster.
    const key = q.project ? `p:${q.project.trim().toLowerCase()}` : `q:${q.id}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)!.push(q);
  }
  const groups: QuoteGroup[] = [];
  for (const rows of byKey.values()) {
    const amounts = rows.map((r) => r.amount_cents);
    groups.push({
      // Display label comes from the first row's original casing.
      project: rows[0].project,
      quotes: [...rows].sort((a, b) => a.amount_cents - b.amount_cents),
      minCents: Math.min(...amounts),
      maxCents: Math.max(...amounts),
      latestAt: Math.max(...rows.map((r) => r.quoted_on)),
    });
  }
  groups.sort((a, b) => b.latestAt - a.latestAt);
  return groups;
}

/**
 * Auto-expire pass. Marks open quotes whose valid_until has lapsed as
 * 'expired'. Called on list load — cheap single UPDATE, keeps status
 * truthful without a background job. Returns count flipped (0 almost
 * always).
 */
export async function expireLapsedQuotes(
  db: SQLiteDatabase,
  propertyId: string
): Promise<number> {
  const t = now();
  const res = await db.runAsync(
    `UPDATE quote SET status = 'expired', updated_at = ?
     WHERE property_id = ? AND deleted_at IS NULL
       AND status = 'open' AND valid_until IS NOT NULL AND valid_until < ?`,
    t,
    propertyId,
    t
  );
  return res.changes ?? 0;
}

/**
 * Turn a quote into a real expense. The expense inherits vendor,
 * category, amount, system link, and the scanned document (the estimate
 * scan doubles as the receipt until the user swaps in a real invoice).
 * The quote is NOT deleted — status flips to 'converted' and
 * converted_expense_id links the two, preserving price history.
 *
 * `paidOn` defaults to today: the user is converting because the work
 * happened / the bill got paid, which is rarely the quote date.
 * Returns the new expense id.
 */
export async function convertQuoteToExpense(
  db: SQLiteDatabase,
  quote: QuoteRow,
  opts?: { paidOn?: number; amountCents?: number }
): Promise<string> {
  const expenseId = await addExpense(db, {
    propertyId: quote.property_id,
    systemId: quote.system_id,
    category: quote.category,
    vendor: quote.vendor,
    amount_cents: opts?.amountCents ?? quote.amount_cents,
    paid_on: opts?.paidOn ?? now(),
    description: quote.description ?? quote.project ?? quote.vendor ?? 'From quote',
    recurring: null,
    tax_deductible: 0,
    receipt_doc_id: quote.quote_doc_id,
    notes: quote.notes,
  });
  await updateQuote(db, quote.id, {
    status: 'converted',
    converted_expense_id: expenseId,
  });
  return expenseId;
}
